import React, { useState } from 'react';
import { imageUrlTester } from '../lib/imageUrlTester';

interface ImageErrorHandlerProps {
  error: Error;
  imageUrl?: string;
  onRetry?: () => void;
  onUploadInstead?: () => void;
  onClose: () => void;
}

export const ImageErrorHandler: React.FC<ImageErrorHandlerProps> = ({
  error,
  imageUrl,
  onRetry,
  onUploadInstead,
  onClose
}) => {
  const [isDebugging, setIsDebugging] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');

  const handleDebug = async () => {
    if (!imageUrl) return;
    
    setIsDebugging(true);
    setDebugInfo('Debugging image URL...');
    
    try {
      const diagnosis = await imageUrlTester.diagnoseImageUrl(imageUrl);
      setDebugInfo(diagnosis);
    } catch (debugError) {
      setDebugInfo(`Debug Error: ${debugError instanceof Error ? debugError.message : 'Unknown error'}`);
    }
  };

  const getErrorType = (errorMessage: string): string => {
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      return 'CORS Issue';
    } else if (errorMessage.includes('forbidden')) {
      return 'Access Forbidden';
    } else if (errorMessage.includes('not found')) {
      return 'Image Not Found';
    } else if (errorMessage.includes('unavailable')) {
      return 'Server Unavailable';
    } else {
      return 'General Error';
    }
  };

  const getErrorGuidance = (errorMessage: string): string => {
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      return 'The image server is blocking access from your browser due to security restrictions.';
    } else if (errorMessage.includes('forbidden')) {
      return 'The image server is blocking cross-origin requests. This is a common security measure.';
    } else if (errorMessage.includes('not found')) {
      return 'The image URL may be expired or the image has been removed from the server.';
    } else if (errorMessage.includes('unavailable')) {
      return 'The image server is temporarily unavailable. Please try again later.';
    } else {
      return 'There was an issue loading the image. This could be due to network connectivity or server restrictions.';
    }
  };

  const errorType = getErrorType(error.message);
  const guidance = getErrorGuidance(error.message);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Image Loading Error</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-red-800">{errorType}</h4>
                <p className="text-sm text-red-700 mt-1">{guidance}</p>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p><strong>Error:</strong> {error.message}</p>
            {imageUrl && (
              <p className="mt-1"><strong>URL:</strong> <span className="font-mono text-xs break-all">{imageUrl}</span></p>
            )}
          </div>

          {isDebugging && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Debug Information</h4>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">{debugInfo}</pre>
            </div>
          )}
        </div>

        <div className="flex flex-col space-y-2">
          {onRetry && (
            <button
              onClick={onRetry}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          )}
          
          {onUploadInstead && (
            <button
              onClick={onUploadInstead}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Upload Image Instead
            </button>
          )}
          
          {imageUrl && (
            <button
              onClick={handleDebug}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {isDebugging ? 'Hide Debug Info' : 'Debug Image URL'}
            </button>
          )}
          
          <button
            onClick={onClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}; 